/* eslint-disable react/destructuring-assignment */
import React, { useEffect, useContext } from 'react';
import PropTypes from 'prop-types';
import { SelectList } from '@abyss/ui/form/SelectList';
import { useForm } from '@abyss/ui/form/hooks/useForm';
import { useCorporatesSearch } from 'src/hooks/useCorporatesSearch';
import { styled } from '@abyss/ui/tools/styled';
import { AlertContext } from 'src/tools/AlertContext';

const CustomLabel = styled.label({
  fontSize: ({ theme }) => theme.fontSizes.size400,
  color: ({ theme }) => theme.colors.UHC_GRAY_DARK,
  fontWeight: 600,
});

export const Corporates = ({
  uuid,
  selectedProvider,
  setSelectedProvider,
  setAllowProviderSearch,
}) => {
  const form = useForm();
  const errorContext = useContext(AlertContext);

  const { organizationId } = selectedProvider;
  const [corporatesResult, getCorporates] = useCorporatesSearch({
    onCompleted: data => {
      if (data.length === 1) {
        const firstCorp = data[0];
        form.set('utility.corporateProvider', firstCorp);
        setSelectedProvider(prevProvider => {
          if (prevProvider.corporateProviderMpin === firstCorp.mpin) {
            return prevProvider;
          }

          return {
            ...selectedProvider,
            ...(firstCorp.uhcProviderId && {
              uhcProviderId: firstCorp.uhcProviderId,
            }),
            ...(firstCorp.id && {
              uhcOrgId: firstCorp.id,
            }),
            organizationId,
            corporateProviderMpin: firstCorp.mpin,
            uniqueId: firstCorp.uniqueId,
          };
        });
      }
    },
    onError: err => {
      errorContext.addAlert(err);
    },
  });
  const corporates = corporatesResult.data || [];

  useEffect(() => {
    if (uuid) {
      getCorporates({
        variables: JSON.stringify({ uuid }),
      });
    } else {
      form.reset('utility.corporateProvider');
    }
  }, [uuid, organizationId]);

  const handleChange = selectedCorp => {
    const corporateProviderMpin = selectedCorp ? selectedCorp.mpin : null;
    if (selectedProvider.uniqueId !== selectedCorp.uniqueId) {
      const newData = {
        ...(organizationId && {
          organizationId,
        }),
        corporateProviderMpin,
        ...(selectedCorp.id && {
          uhcOrgId: selectedCorp.id,
        }),
        ...(selectedCorp.uhcProviderId && {
          uhcProviderId: selectedCorp.uhcProviderId,
        }),
        ...(selectedCorp.uniqueId && {
          uniqueId: selectedCorp.uniqueId,
        }),
      };

      setSelectedProvider(newData);

      form.reset('utility.taxIdNumber');
    }
    setAllowProviderSearch(true);
  };

  return (
    <React.Fragment>
      <CustomLabel
        id="corporate-tax-owner-header"
        data-testid="corporate-tax-owner-header"
        htmlFor="provider-select-corporates"
      >
        Corporate Tax ID Owner
      </CustomLabel>
      <SelectList
        id="provider-select-corporates"
        data-testid="provider-select-corporates"
        data-cy="provider-select-corporates"
        model="utility.corporateProvider"
        aria-label="change corporate tax ID owner"
        aria-labelledby="corporate-tax-owner-header"
        aria-required="true"
        options={corporates}
        placeholder="Select Corporations..."
        valueKey="uniqueId"
        isDisabled={corporatesResult.isLoading}
        isLoading={corporatesResult.isLoading}
        onChange={handleChange}
        validators={{ required: value => !value }}
      />
    </React.Fragment>
  );
};

Corporates.propTypes = {
  uuid: PropTypes.string.isRequired,
  selectedProvider: PropTypes.shape({
    organizationId: PropTypes.string,
    corporateProviderMpin: PropTypes.string,
    uniqueId: PropTypes.string,
  }),
  setSelectedProvider: PropTypes.func.isRequired,
  setAllowProviderSearch: PropTypes.func.isRequired,
};

Corporates.defaultProps = {
  selectedProvider: null,
};
